import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { Observable } from 'rxjs';

// services
import { TranslateService } from '@ngx-translate/core';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { PopoverService } from '@app/shared/services/popover.service';

// Types

import {
  ProfileCollectionFacade,
  ProfileCollectionViewModel,
} from '@app/profile/components/profile-collection/profile-collection-reactive-store';
import {
  CollectionFacet,
  CollectionFacetValue,
} from '@app/profile/profile-api.model';
import { DfButtonBasicComponent } from '@lib/fresco';

@Component({
  selector: 'dgx-facet-filters',
  templateUrl: './facet-filters.component.html',
  styleUrls: [
    './facet-filters.component.scss',
    '../profile-collection-filters.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
// @todo try to make this more data driven so new filters can be added more easily
export class FacetFilters
  extends SubscriberBaseDirective
  implements AfterViewInit, OnInit, OnChanges
{
  @Input() public filterToFocus: string;

  @ViewChildren('filterToggle')
  public filterToggles: QueryList<DfButtonBasicComponent>;

  public vm$: Observable<ProfileCollectionViewModel>;

  public searchTerms = { tags: '', providers: '', experienceType: '' };
  public numFacetsBeforeShowSearch = 10;

  public i18n = this.translateService.instant([
    'Core_ShowResults',
    'Core_Search',
    'dgAssignmentsOverview_AssignmentEmptyTitle',
    'LearningSearch_ClearFilters',
  ]);

  public filterItems: {
    [key: string]: string[];
  } = {};

  constructor(
    private translateService: TranslateService,
    private profileCollectionFacade: ProfileCollectionFacade,
    private popover: PopoverService
  ) {
    super();
  }

  public showSearch(facet: CollectionFacet): boolean {
    // we only show the search bar within the filter if there are more than 10 facet values as we only show up to the
    // top ten facet values in each filter, the others can be found using the search function.
    return (
      facet.values.length > this.numFacetsBeforeShowSearch ||
      !!this.searchTerms[facet.id]
    );
  }

  public ngOnInit(): void {
    this.vm$ = this.profileCollectionFacade.vm$;
    this.vm$.pipe(this.takeUntilDestroyed()).subscribe((vm) => {
      for (const key in vm.filter) {
        if (Array.isArray(vm.filter[key])) {
          this.filterItems[key] = vm.filter[key];
        }
      }
    });
    this.initSearchTerms();
  }

  public ngAfterViewInit() {
    this.setupFocus(this.filterToFocus);
  }

  public ngOnChanges(changes: SimpleChanges) {
    const filterToFocus = changes?.filterToFocus?.currentValue;
    this.setupFocus(filterToFocus);
  }

  public setupFocus(filterToFocus: string) {
    if (
      ['tags', 'providers', 'experienceType'].includes(filterToFocus) &&
      this.filterToggles
    ) {
      this.filterToggles
        .find(
          (t) => t?.elementRef?.nativeElement.id === `${filterToFocus}-button`
        )
        .elementRef?.nativeElement.focus();
    }
  }

  public initSearchTerms(): void {
    this.searchTerms = {
      tags: '',
      providers: '',
      experienceType: '',
    };
  }

  public getFacetLabel(facet: CollectionFacet): string {
    if (facet.name === 'ExperienceType') {
      // TODO: This is a hack to get the correct translation for the ExperienceType facet until we can get access to
      //  the i18n tool to fix the key
      return this.translateService.instant('Core_Type');
    }
    return facet.label
      ? this.translateService.instant(facet.label)
      : this.translateService.instant(`Core_${facet.name}`);
  }

  public getHeading(facet: CollectionFacet): string {
    if (facet.name === 'ExperienceType') {
      return this.translateService.instant('Core_ExperiencesDisplayName');
    }
    return facet.label
      ? this.translateService.instant(facet.label)
      : this.translateService.instant(`Core_${facet.name}`);
  }

  public filterToggled(
    event: Event,
    facet: CollectionFacet,
    item: CollectionFacetValue
  ): void {
    event.stopPropagation();
    if (!this.filterItems[facet.id]) {
      this.filterItems[facet.id] = [];
    }

    const isFilterItemSelected = this.filterItems[facet.id].includes(item.id);

    if (isFilterItemSelected) {
      this.filterItems[facet.id] = this.filterItems[facet.id].filter(
        (i) => i !== item.id
      );
    } else {
      this.filterItems[facet.id] = [...this.filterItems[facet.id], item.id];
    }
  }

  public resetFilter(facetId: string): void {
    this.profileCollectionFacade.resetFilterValue(facetId);
    this.closePopover(facetId);
  }

  public search(facetId: string, searchTerm: string): void {
    this.searchTerms[facetId] = searchTerm;
    this.profileCollectionFacade.searchFacets(facetId, searchTerm);
  }

  public closePopover(facetId: string): void {
    this.profileCollectionFacade.updateFilterValues(
      facetId,
      this.filterItems[facetId]
    );
    this.popover.close();
  }

  public trackById(facet): string {
    return facet.id;
  }
}
